import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { showSuccess } from '@/components/common/Toast';

export interface ProjectForecast {
  id: string;
  projectId: string;
  month: string; // YYYY-MM
  forecastDays: number; // in days (decimal, already converted from centdays)
  forecastValue: number; // in cents
  actualDays: number;
  actualValue: number;
  notes: string | null;
}

interface ForecastsResponse {
  forecasts: ProjectForecast[];
}

interface UpdateForecastInput {
  projectId: string;
  month: string;
  forecastDays?: number;
  forecastValue?: number;
  actualDays?: number;
  actualValue?: number;
  notes?: string;
}

/**
 * Fetch all forecasts for a project
 */
export function useProjectForecasts(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'forecasts'],
    queryFn: () => api.get<ForecastsResponse>(`/projects/${projectId}/forecasts`),
    enabled: !!projectId,
  });
}

/**
 * Update or create a forecast for a specific month
 */
export function useUpdateForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, month, ...data }: UpdateForecastInput) =>
      api.put<{ forecast: ProjectForecast }>(`/projects/${projectId}/forecasts/${month}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'forecasts'] });
      showSuccess('Forecast updated');
    },
  });
}

/**
 * Generate an array of month strings for forecast editing
 * Returns 12 months starting from the given month (or current month)
 */
export function generateMonthRange(startMonth?: string, count = 12): string[] {
  const start = startMonth ? new Date(startMonth + '-01') : new Date();
  start.setDate(1); // Ensure first of month

  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    const date = new Date(start);
    date.setMonth(start.getMonth() + i);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }

  return months;
}

/**
 * Format a month string (YYYY-MM) for display
 */
export function formatMonth(month: string): string {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}
